import { ActionContext } from 'vuex';
import { IListingScore } from '@/shared/interfaces/scoring-overall-result';
import { StoreBaseImplementation } from '@/shared/classes/store-base-implementation';
import listingScore from '@/shared/services/listing-scores';
import { QueryStringCommandOptions, IScoringRuleSet } from '@/shared';
import { IListingScorePayload } from '@/shared/interfaces/listing-score-payload';
import { IListingScoreState } from '@/shared/interfaces/listing-score-store';
import { downloadCSV } from '@/shared/helpers/download-csv';

// Base store configuration
const adminGatewayResourcePath: string = '/listing-scores-api';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: QueryStringCommandOptions = {};

const defaultGetSingleFilters: QueryStringCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementation<IListingScore>({
  adminGatewayResourcePath,
  listViewName,
  showLogging,
  defaultGetSingleFilters,
  defaultGetListFilters,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'listing-scores-api/'

  state: {
    ...base.state,
    clientScoresSummary: null,
    isListingScoresApiAvailable: true,
  },

  getters: {
    ...base.getters,
    ...{
      clientScoresSummary(state: IListingScoreState) {
        return state.clientScoresSummary
          ? state.clientScoresSummary.data
          : {};
      },

      isListingScoresApiAvailable(state: IListingScoreState) {
        return !!state.isListingScoresApiAvailable;
      },
    },
  },

  mutations: {
    ...base.mutations,
    ...{
      setListingScores(state: IListingScoreState, payload: any) {
        state.clientScoresSummary = payload;
      },

      setIsListingScoresApiAvailable(state: IListingScoreState, payload: boolean) {
        state.isListingScoresApiAvailable = payload;
      },
    },
  },

  actions: {
    ...base.actions,

    getClientScoresSummary: async (
      ctx: ActionContext<IListingScoreState, any>,
      payload: IListingScorePayload,
    ): Promise<Record<string, IListingScore> | undefined> => {
      const response = await listingScore.getClientScoresSummary(payload);
      ctx.commit('setListingScores', response);
      return response.data;
    },
    getScoreSummaryCSV: async (ctx: ActionContext<IListingScoreState, any>, payload: IListingScorePayload) => {
      const response = await listingScore.getScoreSummaryCSV(payload);
      downloadCSV(response, `${payload.filename}.csv`);
    },
    getListingScoresRules: async (ctx: ActionContext<IListingScoreState, any>, payload: { version: string }): Promise<IScoringRuleSet | undefined> => {
      const response = await listingScore.getRules(payload.version || 'latest');
      return response.data;
    },
    refreshListingScoreSingle: async (
      ctx: ActionContext<IListingScoreState, any>,
      payload: IListingScorePayload,
    ): Promise<IListingScore> => {
      const response = await listingScore.refreshListingScoreSingle(payload);
      ctx.commit('set', response);
      return response.data;
    },
    getListingScoreSingle: async (
      ctx: ActionContext<IListingScoreState, any>,
      payload: IListingScorePayload,
    ): Promise<IListingScore | null | undefined> => {
      const response = await listingScore.getListingScoreSingle(payload);
      ctx.commit('set', response);
      return response!;
    },

    refreshAndPollListingScoreSingle: async (
      ctx: ActionContext<IListingScoreState, any>,
      payload: IListingScorePayload & { previousEvaluatedAt?: string | Date },
    ): Promise<IListingScore | null> => {
      const { previousEvaluatedAt, ...scorePayload } = payload;
      const result = await listingScore.refreshAndPollListingScoreSingle(scorePayload, previousEvaluatedAt);
      if (result) {
        ctx.commit('set', result);
      }
      return result;
    },

    pollForUpdatedScore: async (
      ctx: ActionContext<IListingScoreState, any>,
      payload: IListingScorePayload & { previousEvaluatedAt?: string | Date },
    ): Promise<IListingScore | null> => {
      const { previousEvaluatedAt, ...scorePayload } = payload;
      const result = await listingScore.pollForUpdatedScore(scorePayload, previousEvaluatedAt);
      if (result) {
        ctx.commit('set', result);
      }
      return result;
    },

  },
};
